import React, { useEffect, useState } from 'react';
import { ImageCompressProps } from '../types';
import { useImageProcessor } from '../hooks/useImageProcessor';

const ImageCompress: React.FC<ImageCompressProps> = ({
    image,
    quality = 80,
    maxSizeMB,
    maxWidthOrHeight,
    onCompressProgress,
    showProgress = true,
    onProcessingComplete,
    onError,
    className,
    style,
}) => {
    const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
    const [compressionProgress, setCompressionProgress] = useState<number>(0);
    const [compressionStats, setCompressionStats] = useState<{
        originalSize: number;
        compressedSize: number;
        compressionRatio: number;
    } | null>(null);

    const { imageBlob, imageUrl, isLoading, compressImage } = useImageProcessor({
        image,
        onError,
    });

    useEffect(() => {
        const compressImageWithProgress = async () => {
            if (!imageBlob) return;

            try {
                // Start progress indication
                setCompressionProgress(10);
                if (onCompressProgress) onCompressProgress(10);

                // Perform compression
                const compressedBlob = await compressImage(quality, maxSizeMB);

                // Update progress
                setCompressionProgress(90);
                if (onCompressProgress) onCompressProgress(90);

                // Calculate compression stats
                const originalSize = imageBlob.size;
                const compressedSize = compressedBlob.size;
                const compressionRatio = (1 - (compressedSize / originalSize)) * 100;

                setCompressionStats({
                    originalSize,
                    compressedSize,
                    compressionRatio,
                });

                // Generate URL for display
                const compressedUrl = URL.createObjectURL(compressedBlob);
                setProcessedImageUrl(compressedUrl);

                // Final progress
                setCompressionProgress(100);
                if (onCompressProgress) onCompressProgress(100);

                // Callback with the compressed image
                if (onProcessingComplete) {
                    onProcessingComplete(compressedBlob);
                }
            } catch (err) {
                console.error('Error compressing image:', err);
                if (onError && err instanceof Error) {
                    onError(err);
                }
            }
        };

        if (imageBlob) {
            compressImageWithProgress();
        }

        // Cleanup function
        return () => {
            if (processedImageUrl) {
                URL.revokeObjectURL(processedImageUrl);
            }
        };
    }, [imageBlob, quality, maxSizeMB, onCompressProgress, onProcessingComplete, onError, compressImage]);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (isLoading) {
        return <div className={className} style={style}>Loading image...</div>;
    }

    return (
        <div
            className={className}
            style={{
                ...style,
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Original Image */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>Original</h3>
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt="Original"
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                border: '1px solid #ddd',
                            }}
                        />
                    )}
                    {imageBlob && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            Size: {formatFileSize(imageBlob.size)}
                        </div>
                    )}
                </div>

                {/* Compressed Image */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>Compressed (Quality: {quality}%)</h3>
                    {processedImageUrl ? (
                        <img
                            src={processedImageUrl}
                            alt="Compressed"
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                border: '1px solid #ddd',
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #ddd',
                        }}>
                            Compressing...
                        </div>
                    )}
                    {compressionStats && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            <div>Size: {formatFileSize(compressionStats.compressedSize)}</div>
                            <div>Reduced by: {compressionStats.compressionRatio.toFixed(1)}%</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Progress Bar */}
            {showProgress && (
                <div style={{ marginTop: '0.5rem' }}>
                    <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${compressionProgress}%`,
                            height: '100%',
                            backgroundColor: '#4caf50',
                            transition: 'width 0.3s ease-in-out',
                        }} />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                        color: '#666',
                        marginTop: '4px',
                    }}>
                        <span>0%</span>
                        <span>{compressionProgress}%</span>
                        <span>100%</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageCompress; 